import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ShoppingBag, Minus, Plus, Trash2, Loader2, ExternalLink } from "lucide-react";
import { useCartStore } from "@/stores/cartStore";
import { useHydrated } from "@/hooks/useHydrated";
import { formatMoney } from "@/lib/shopify";
import { ResponsiveImage } from "@/components/responsive-image";
import { SIZES } from "@/lib/image-sources";

export function CartDrawer() {
  const [open, setOpen] = useState(false);
  const { items, isLoading, isSyncing, updateQuantity, removeItem, getCheckoutUrl, syncCart } =
    useCartStore();
  const hydrated = useHydrated();
  const totalItems = hydrated ? items.reduce((s, i) => s + i.quantity, 0) : 0;
  const total = items.reduce((s, i) => s + parseFloat(i.price.amount) * i.quantity, 0);
  const currency = items[0]?.price.currencyCode ?? "USD";

  useEffect(() => {
    if (open) syncCart();
  }, [open, syncCart]);

  const handleCheckout = () => {
    const url = getCheckoutUrl();
    if (url) {
      // Same-tab checkout — best practice for conversion (keeps users in one flow).
      window.location.href = url;
      setOpen(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          aria-label="Open cart"
          className="relative inline-flex h-10 w-10 items-center justify-center text-parchment/80 transition-colors hover:text-gold"
        >
          <ShoppingBag className="h-5 w-5" />
          {totalItems > 0 && (
            <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-gold px-1 font-display text-[0.65rem] text-ink">
              {totalItems}
            </span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent className="flex w-full flex-col bg-background sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="font-display text-xl tracking-[0.2em] text-parchment">
            Your Cart
          </SheetTitle>
          <SheetDescription className="text-muted-foreground">
            {totalItems === 0
              ? "The cart is empty."
              : `${totalItems} item${totalItems === 1 ? "" : "s"} held in waiting.`}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 flex min-h-0 flex-1 flex-col">
          {!hydrated || items.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center text-center">
              <ShoppingBag className="h-10 w-10 text-muted-foreground/60" />
              <p className="mt-4 text-sm text-muted-foreground">No bottles selected yet.</p>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto pr-2">
                <ul className="space-y-5">
                  {items.map((item) => (
                    <li key={item.variantId} className="flex gap-4 border-b border-border/60 pb-5">
                      <div className="h-20 w-20 flex-shrink-0 overflow-hidden border border-border bg-card">
                        {item.image && (
                          <ResponsiveImage
                            src={item.image.url}
                            alt={item.image.altText || `${item.productTitle} — bottle photograph`}
                            sizes={SIZES.cartThumb}
                            widths={[96, 192, 288]}
                            pictureClassName="contents"
                            className="h-full w-full object-cover"
                          />
                        )}
                      </div>
                      <div className="flex flex-1 flex-col">
                        <p className="font-display text-sm tracking-[0.12em] text-parchment">
                          {item.productTitle}
                        </p>
                        {item.variantTitle && item.variantTitle !== "Default Title" && (
                          <p className="mt-1 text-xs text-muted-foreground">{item.variantTitle}</p>
                        )}
                        <p className="mt-1 text-sm text-gold">
                          {formatMoney(item.price.amount, item.price.currencyCode)}
                        </p>
                        <div className="mt-auto flex items-center justify-between pt-3">
                          <div className="inline-flex items-center border border-border">
                            <button
                              type="button"
                              onClick={() => updateQuantity(item.variantId, item.quantity - 1)}
                              className="px-2 py-1 text-muted-foreground hover:text-gold"
                              aria-label="Decrease"
                            >
                              <Minus className="h-3 w-3" />
                            </button>
                            <span className="px-3 font-display text-xs text-parchment">
                              {item.quantity}
                            </span>
                            <button
                              type="button"
                              onClick={() => updateQuantity(item.variantId, item.quantity + 1)}
                              disabled={
                                typeof item.maxQuantity === "number" &&
                                item.quantity >= item.maxQuantity
                              }
                              className="px-2 py-1 text-muted-foreground hover:text-gold disabled:cursor-not-allowed disabled:opacity-40"
                              aria-label="Increase"
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeItem(item.variantId)}
                            className="text-muted-foreground hover:text-gold"
                            aria-label="Remove"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-6 space-y-4 border-t border-border pt-4">
                <div className="flex items-center justify-between">
                  <span className="eyebrow">Subtotal</span>
                  <span className="font-display text-lg text-parchment">
                    {formatMoney(total, currency)}
                  </span>
                </div>
                <Button
                  onClick={handleCheckout}
                  disabled={isLoading || isSyncing}
                  className="w-full rounded-sm bg-gradient-to-r from-gold-soft to-gold py-6 font-display text-xs uppercase tracking-[0.24em] text-ink hover:opacity-90"
                >
                  {isLoading || isSyncing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Checkout
                    </>
                  )}
                </Button>
                <Link
                  to="/cart"
                  onClick={() => setOpen(false)}
                  className="block text-center font-display text-xs uppercase tracking-[0.24em] text-gold underline-offset-4 hover:underline"
                >
                  View cart
                </Link>
                <p className="text-center text-xs text-muted-foreground">
                  Shipping & taxes calculated at checkout.
                </p>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
